/* eslint-disable max-lines */
import { observable } from 'mobx';
import moment from 'moment';
import type { FormData as OnlineApplicationFormData } from '@this/components/admin/online_applications/shared/types';
import type { OnlineApplicationCompany } from './online_application_company';
import type { OnlineApplicationApprovalLogArgs } from './online_application_approval_log';
import OnlineApplicationApprovalLog from './online_application_approval_log';
import type { OnlineApplicationBillingArgs } from './online_application_billing';
import OnlineApplicationBilling from './online_application_billing';
import type { OnlineApplicationInitialCostArgs } from './online_application_initial_cost';
import OnlineApplicationInitialCost from './online_application_initial_cost';
import type { OnlineApplicationPlanArgs } from './online_application_plan';
import OnlineApplicationPlan from './online_application_plan';
import type { OnlineApplicationApiResponse } from './api_types';

// FormDataインターフェース（shared/types.tsから再エクスポート）

export interface AdditionalCompany {
  id: string;
  name: string;
  nameKana: string;
  postalCode: string;
  address: string;
  tel: string;
  contactName: string;
  contactNameKana: string;
  contactTel: string;
  contactEmail: string;
}

export interface DynamicOption {
  id: string;
  optionId: string;
  price: number;
}
export type FormData = OnlineApplicationFormData;

export interface OnlineApplicationPreviewData {
  startDesiredMonth: string;
  companyName: string;
  companyNameKana: string;
  companyPostalCode: string;
  companyAddress: string;
  companyTel: string;
  contactName: string;
  contactNameKana: string;
  contactTel: string;
  contactEmail: string;
  contactDepartment: string;
  isBillingSameAsCompany: boolean;
  billingPostalCode: string;
  billingAddress: string;
  billingTel: string;
  billingDepartment: string;
  billingContactName: string;
  billingContactNameKana: string;
  billingContactTel: string;
  billingContactEmail: string;
  additionalCompanies: AdditionalCompany[];
  initialCostLabel?: string;
  initialCostPrice?: number;
  initialCostPlanId?: number;
  basicPlanLabel?: string;
  basicPlanPrice?: number;
  basicPlanId?: string;
  showSupportFee?: boolean;
  supportFeeName?: string;
  invoicePaymentRequested: boolean;
  dynamicOptions?: DynamicOption[];
  bankName: string;
  branchName: string;
  accountType: string;
  accountNumber: string;
  accountHolderName: string;
  creditDownComment?: string;
  blockingFactorComment?: string;
  overrideJustificationComment?: string;
  otherComment?: string;
  potentialGmv: number;
  salesforceUrl?: string;
  existence: string;
  slackUrl?: string;
  applicationDate?: string;
}

export interface OnlineApplicationApiData {
  id: number;
  status: string;
  start_desired_month: string;
  company_name: string;
  company_name_kana: string;
  company_postal_code: string;
  company_address: string;
  company_tel: string;
  contact_name: string;
  contact_name_kana: string;
  contact_tel: string;
  contact_email: string;
  contact_department: string;
  is_billing_same_as_company: boolean;
  billing_postal_code: string;
  billing_address: string;
  billing_tel: string;
  billing_department: string;
  billing_contact_name: string;
  billing_contact_name_kana: string;
  billing_contact_tel: string;
  billing_contact_email: string;
  additional_companies: AdditionalCompany[];
  initial_cost_label?: string;
  initial_cost_price?: number;
  initial_cost_plan_id?: number;
  basic_plan_label?: string;
  basic_plan_price?: number;
  basic_plan_id?: string;
  selected_options: string[];
  show_support_fee?: boolean;
  support_fee_name?: string;
  invoice_payment_requested?: boolean;
  dynamic_options?: DynamicOption[];
  bank_name: string;
  branch_name: string;
  account_type: string;
  account_number: string;
  account_holder_name: string;
  credit_down_comment?: string;
  blocking_factor_comment?: string;
  override_justification_comment?: string;
  other_comment?: string;
  potential_gmv?: number;
  salesforce_url?: string;
  existence?: string;
  slack_url?: string;
  application_date?: string;
  created_at: string;
  updated_at: string;
}

export interface OnlineApplicationArgs {
  id: number;
  status: string;
  applicantId: number;
  applicantName?: string;
  invoicePaymentRequested?: boolean;
  startDesiredMonth?: string;
  companyName: string;
  companyNameKana: string;
  companyPostalCode: string;
  companyAddress: string;
  companyTel: string;
  contactName: string;
  contactNameKana: string;
  contactTel: string;
  contactEmail: string;
  contactDepartment?: string;
  isBillingSameAsCompany?: boolean;
  billingPostalCode?: string;
  billingAddress?: string;
  billingTel?: string;
  billingDepartment?: string;
  billingContactName?: string;
  billingContactNameKana?: string;
  billingContactTel?: string;
  billingContactEmail?: string;
  initialCostPrice?: number;
  initialCostLabel?: string;
  basicPlanId?: number;
  basicPlanLabel?: string;
  basicPlanPrice?: number;
  dynamicOptions?: DynamicOption[];
  showSupportFee?: boolean;
  supportFeeName?: string;
  bankName?: string;
  branchName?: string;
  accountType?: string;
  accountNumber?: string;
  accountHolderName?: string;
  salesforceUrl?: string;
  existence?: string;
  creditDownComment?: string;
  blockingFactorComment?: string;
  overrideJustificationComment?: string;
  otherComment?: string;
  slackUrl?: string;
  applicationDate?: string;
  potentialGmv?: number;
  expirationDate?: string;
  applicationEmailAddress?: string;
  applicationWorkflowId?: number | null;
  orderWorkflowId?: number | null;
  appliedAt?: string;
  approvedAt?: string;
  createdAt?: string;
  updatedAt?: string;
  additionalCompanies?: AdditionalCompany[];
  selectedOptions?: string[];
  onlineApplicationCompanies?: OnlineApplicationCompany[];
  approvalLogs?: OnlineApplicationApprovalLogArgs[];
  companies?: any[];
  billing?: OnlineApplicationBillingArgs | null;
  plans?: OnlineApplicationPlanArgs[];
  options?: any[];
  initialCosts?: OnlineApplicationInitialCostArgs[];
}

export class OnlineApplication {
  @observable id: number;
  @observable status: string;
  @observable applicantId: number;
  @observable applicantName?: string;
  @observable invoicePaymentRequested: boolean;
  @observable startDesiredMonth?: string;
  @observable companyName: string;
  @observable companyNameKana: string;
  @observable companyPostalCode: string;
  @observable companyAddress: string;
  @observable companyTel: string;
  @observable contactName: string;
  @observable contactNameKana: string;
  @observable contactTel: string;
  @observable contactEmail: string;
  @observable contactDepartment: string;
  @observable isBillingSameAsCompany: boolean;
  @observable billingPostalCode: string;
  @observable billingAddress: string;
  @observable billingTel: string;
  @observable billingDepartment: string;
  @observable billingContactName: string;
  @observable billingContactNameKana: string;
  @observable billingContactTel: string;
  @observable billingContactEmail: string;
  @observable initialCostLabel?: string;
  @observable initialCostPrice: number;
  @observable basicPlanId?: number;
  @observable basicPlanLabel?: string;
  @observable basicPlanPrice?: number;
  @observable showSupportFee?: boolean;
  @observable supportFeeName?: string;
  @observable dynamicOptions?: DynamicOption[];
  @observable bankName: string;
  @observable branchName: string;
  @observable accountType: string;
  @observable accountNumber: string;
  @observable accountHolderName: string;
  @observable creditDownComment?: string;
  @observable blockingFactorComment?: string;
  @observable overrideJustificationComment?: string;
  @observable otherComment?: string;
  @observable salesforceUrl?: string;
  @observable existence: string;
  @observable slackUrl: string;
  @observable applicationDate?: string;
  @observable potentialGmv?: number;
  @observable expirationDate?: string;
  @observable applicationEmailAddress?: string;
  @observable applicationWorkflowId?: number | null;
  @observable orderWorkflowId?: number | null;
  @observable submitterCompanyName?: string;
  @observable submitterDepartment?: string;
  @observable submitterName?: string;
  @observable submitterEmail?: string;
  @observable appliedAt?: string;
  @observable approvedAt?: string;
  @observable createdAt: string;
  @observable updatedAt: string;
  @observable additionalCompanies: AdditionalCompany[];
  @observable onlineApplicationCompanies: OnlineApplicationCompany[];
  @observable approvalLogs: OnlineApplicationApprovalLog[];
  @observable companies: any[];
  @observable billing: OnlineApplicationBilling | null;
  @observable plans: OnlineApplicationPlan[];
  @observable options: any[];
  @observable initialCosts: OnlineApplicationInitialCost[];

  constructor(args: Partial<OnlineApplicationArgs> | any = {}) {
    // APIレスポンスから直接渡される場合はsnake_case、
    // 内部変換済みの場合はcamelCaseなので、両方に対応
    this.id = args.id || 0;
    this.status = args.status || '';
    this.applicantId = args.applicantId || args.applicant_id || 0;
    this.applicantName = args.applicantName || args.applicant_name;
    this.invoicePaymentRequested = args.invoicePaymentRequested || args.invoice_payment_requested || false;
    this.startDesiredMonth = args.startDesiredMonth || args.start_desired_month;
    this.companyName = args.companyName || args.company_name || '';
    this.companyNameKana = args.companyNameKana || args.company_name_kana || '';
    this.companyPostalCode = args.companyPostalCode || args.company_postal_code || '';
    this.companyAddress = args.companyAddress || args.company_address || '';
    this.companyTel = args.companyTel || args.company_tel || '';
    this.contactName = args.contactName || args.contact_name || '';
    this.contactNameKana = args.contactNameKana || args.contact_name_kana || '';
    this.contactTel = args.contactTel || args.contact_tel || '';
    this.contactEmail = args.contactEmail || args.contact_email || '';
    this.contactDepartment = args.contactDepartment || args.contact_department || '';
    this.isBillingSameAsCompany = args.isBillingSameAsCompany || args.is_billing_same_as_company || false;
    this.billingPostalCode = args.billingPostalCode || args.billing_postal_code || '';
    this.billingAddress = args.billingAddress || args.billing_address || '';
    this.billingTel = args.billingTel || args.billing_tel || '';
    this.billingDepartment = args.billingDepartment || args.billing_department || '';
    this.billingContactName = args.billingContactName || args.billing_contact_name || '';
    this.billingContactNameKana = args.billingContactNameKana || args.billing_contact_name_kana || '';
    this.billingContactTel = args.billingContactTel || args.billing_contact_tel || '';
    this.billingContactEmail = args.billingContactEmail || args.billing_contact_email || '';
    this.initialCostPrice = args.initialCostPrice || args.initial_cost_price || 0;
    this.initialCostLabel = args.initialCostLabel || args.initial_cost_label;
    this.basicPlanId = args.basicPlanId || args.basic_plan_id;
    this.basicPlanLabel = args.basicPlanLabel || args.basic_plan_label;
    this.basicPlanPrice = args.basicPlanPrice || args.basic_plan_price;
    this.showSupportFee = args.showSupportFee || args.show_support_fee || false;
    this.supportFeeName = args.supportFeeName || args.support_fee_name;
    this.dynamicOptions = args.dynamicOptions || args.dynamic_options;
    this.bankName = args.bankName || args.bank_name || '';
    this.branchName = args.branchName || args.branch_name || '';
    this.accountType = args.accountType || args.account_type || '';
    this.accountNumber = args.accountNumber || args.account_number || '';
    this.accountHolderName = args.accountHolderName || args.account_holder_name || '';
    this.salesforceUrl = args.salesforceUrl || args.salesforce_url;
    this.existence = args.existence || '';
    this.creditDownComment = args.creditDownComment || args.credit_down_comment;
    this.blockingFactorComment = args.blockingFactorComment || args.blocking_factor_comment;
    this.overrideJustificationComment = args.overrideJustificationComment || args.override_justification_comment;
    this.otherComment = args.otherComment || args.other_comment;
    this.slackUrl = args.slackUrl || args.slack_url || '';
    this.applicationDate = args.applicationDate || args.application_date;
    this.potentialGmv = args.potentialGmv || args.potential_gmv;
    this.expirationDate = args.expiration_date;
    this.applicationEmailAddress = args.application_email_address;
    this.applicationWorkflowId = args.applicationWorkflowId ?? args.application_workflow_id ?? null;
    this.orderWorkflowId = args.orderWorkflowId ?? args.order_workflow_id ?? null;
    this.submitterCompanyName = args.submitterCompanyName ?? args.submitter_company_name ?? '';
    this.submitterDepartment = args.submitterDepartment ?? args.submitter_department ?? '';
    this.submitterName = args.submitterName ?? args.submitter_name ?? '';
    this.submitterEmail = args.submitterEmail ?? args.submitter_email ?? '';
    this.appliedAt = args.applied_at;
    this.approvedAt = args.approved_at;
    this.createdAt = args.created_at || '';
    this.updatedAt = args.updated_at || '';
    this.additionalCompanies = args.additional_companies || [];
    this.onlineApplicationCompanies = args.online_application_companies || [];
    this.approvalLogs = args.approval_logs
      ? args.approval_logs.map((data: OnlineApplicationApprovalLogArgs) => new OnlineApplicationApprovalLog(data))
      : [];
    this.companies = args.companies || [];
    this.billing = args.billing ? new OnlineApplicationBilling(args.billing) : null;
    this.plans = args.plans
      ? args.plans.map((data: OnlineApplicationPlanArgs) => new OnlineApplicationPlan(data))
      : [];
    this.options = args.options || [];
    this.initialCosts = args.initial_costs
      ? args.initial_costs.map((data: OnlineApplicationInitialCostArgs) => new OnlineApplicationInitialCost(data))
      : [];
  }

  // ステータス表示用のメソッド
  get statusLabel(): string {
    return OnlineApplication.getStatusLabel(this.status);
  }

  // ステータスからラベルを取得
  static getStatusLabel(status: string): string {
    switch (status) {
      case 'draft':
        return '下書き';
      case 'admin_applied':
        return '申請中';
      case 'first_approved':
        return '1次承認済み';
      case 'final_approved':
        return '最終承認済み';
      case 'user_applied':
        return '顧客申込済み';
      case 'send_back':
        return '差し戻し';
      case 'withdraw':
        return '取り下げ';
      case 'send_email':
        return 'メール送信済み';
      default:
        return status;
    }
  }

  get isFinalApproved(): boolean {
    return this.status === 'final_approved';
  }

  isApplicant(adminId: number): boolean {
    return this.applicantId === adminId;
  }

  canBeFirstApprovedBy(adminId: number): boolean {
    return this.status === 'admin_applied' && !this.isApplicant(adminId);
  }

  canBeFinalApprovedBy(adminId: number): boolean {
    // ステータスが1次承認済みでない、または申請者本人の場合は承認不可
    if (this.status !== 'first_approved' || this.isApplicant(adminId)) {
      return false;
    }

    // 最新の1次承認者でないことを確認
    // approvalLogsは時系列順（新しい順）に並んでいるため、最初のfirst_approvedを取得
    const approvalLogs = this.approvalLogs?.filter(log => log.actionStatus === 'first_approved') || [];
    const latestFirstApprovalLog = approvalLogs[0];

    if (!latestFirstApprovalLog) {
      return false;
    }

    // 最新の1次承認者と現在のユーザーが異なる場合のみ承認可能
    return latestFirstApprovalLog.loggedByAdminId !== adminId;
  }

  // camelCaseデータからsnake_case形式のAPIデータを生成
  static convertToApiData(
    data: OnlineApplicationPreviewData & { id?: number; status?: string }
  ): OnlineApplicationApiData {
    return {
      id: data.id || 0,
      status: data.status || 'draft',
      start_desired_month: data.startDesiredMonth || '',
      company_name: data.companyName,
      company_name_kana: data.companyNameKana,
      company_postal_code: data.companyPostalCode,
      company_address: data.companyAddress,
      company_tel: data.companyTel,
      contact_name: data.contactName,
      contact_name_kana: data.contactNameKana,
      contact_tel: data.contactTel,
      contact_email: data.contactEmail,
      contact_department: data.contactDepartment,
      is_billing_same_as_company: data.isBillingSameAsCompany,
      billing_postal_code: data.billingPostalCode,
      billing_address: data.billingAddress,
      billing_tel: data.billingTel,
      billing_department: data.billingDepartment,
      billing_contact_name: data.billingContactName,
      billing_contact_name_kana: data.billingContactNameKana,
      billing_contact_tel: data.billingContactTel,
      billing_contact_email: data.billingContactEmail,
      additional_companies: data.additionalCompanies || [],
      initial_cost_label: data.initialCostLabel,
      initial_cost_price: data.initialCostPrice || 0,
      basic_plan_label: data.basicPlanLabel,
      basic_plan_price: data.basicPlanPrice,
      basic_plan_id: data.basicPlanId,
      selected_options: [],
      dynamic_options: data.dynamicOptions,
      show_support_fee: data.showSupportFee,
      support_fee_name: data.supportFeeName,
      invoice_payment_requested: data.invoicePaymentRequested,
      bank_name: data.bankName,
      branch_name: data.branchName,
      account_type: data.accountType,
      account_number: data.accountNumber,
      account_holder_name: data.accountHolderName,
      credit_down_comment: data.creditDownComment,
      blocking_factor_comment: data.blockingFactorComment,
      override_justification_comment: data.overrideJustificationComment,
      other_comment: data.otherComment,
      potential_gmv: data.potentialGmv,
      salesforce_url: data.salesforceUrl,
      existence: data.existence,
      slack_url: data.slackUrl,
      application_date: data.applicationDate,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
  }

  /**
   * FormDataをAPIリクエスト用のパラメータに変換する
   */
  static convertFormDataToApiParams(formData: OnlineApplicationFormData, status?: string) {
    return {
      status: status || 'draft',
      start_desired_month: formData.startDesiredMonth
        ? moment(formData.startDesiredMonth).format('YYYY-MM-DD')
        : null,
      potential_gmv: formData.potentialGmv,
      existence: formData.existence,
      credit_down_comment: formData.creditDownComment,
      blocking_factor_comment: formData.blockingFactorComment,
      override_justification_comment: formData.overrideJustificationComment,
      other_comment: formData.otherComment,
      is_billing_same_as_company: formData.isBillingSameAsCompany,
      salesforce_url: formData.salesforceUrl,
      slack_url: formData.slackUrl,
      company_name: formData.companyName,
      company_name_kana: formData.companyNameKana,
      company_postal_code: formData.companyPostalCode,
      company_address: formData.companyAddress,
      company_tel: formData.companyTel,
      contact_name: formData.contactName,
      contact_name_kana: formData.contactNameKana,
      contact_tel: formData.contactTel,
      contact_email: formData.contactEmail,
      contact_department: formData.contactDepartment,
      billing_postal_code: formData.billingPostalCode,
      billing_address: formData.billingAddress,
      billing_tel: formData.billingTel,
      billing_department: formData.billingDepartment,
      billing_contact_name: formData.billingContactName,
      billing_contact_name_kana: formData.billingContactNameKana,
      billing_contact_tel: formData.billingContactTel,
      billing_contact_email: formData.billingContactEmail,
      // initial_cost
      initial_cost_price: formData.initialCostPrice,
      initial_cost_label: formData.initialCostLabel,
      initial_cost_plan_id: formData.initialCostPlanId,
      // 基本プラン
      basic_plan_label: formData.basicPlanLabel,
      basic_plan_price: formData.basicPlanPrice,
      basic_plan_id: formData.basicPlanId,
      show_support_fee: formData.showSupportFee,
      support_fee_name: formData.supportFeeName,
      invoice_payment_requested: formData.invoicePaymentRequested,
      dynamic_options: formData.dynamicOptions?.map(opt => ({
        id: opt.id,
        option_id: opt.optionId,
        price: opt.price
      })),
      bank_name: formData.bankName,
      branch_name: formData.branchName,
      account_type: formData.accountType,
      account_number: formData.accountNumber,
      account_holder_name: formData.accountHolderName,
      additional_companies: formData.additionalCompanies?.map(company => ({
        id: company.id,
        name: company.name,
        name_kana: company.nameKana,
        postal_code: company.postalCode,
        address: company.address,
        tel: company.tel,
        contact_name: company.contactName,
        contact_name_kana: company.contactNameKana,
        contact_tel: company.contactTel,
        contact_email: company.contactEmail,
        contact_department: company.contactDepartment
      }))
    };
  }

  /**
   * APIレスポンスからFormDataに変換する（後方互換性のため維持）
   * @deprecated 新しい convertApiResponseToBaseFormData を使用してください
   */
  static convertApiResponseToFormData(data: any): FormData {
    return {
      // 基本情報
      startDesiredMonth: data.start_desired_month || '',

      // 法人情報（最初の会社情報を使用）
      companyName: data.companies?.[0]?.name || '',
      companyNameKana: data.companies?.[0]?.name_kana || '',
      companyPostalCode: data.companies?.[0]?.postal_code || '',
      companyAddress: data.companies?.[0]?.address || '',
      companyTel: data.companies?.[0]?.tel || '',
      contactName: data.companies?.[0]?.contact_name || '',
      contactNameKana: data.companies?.[0]?.contact_name_kana || '',
      contactTel: data.companies?.[0]?.contact_tel || '',
      contactEmail: data.companies?.[0]?.contact_email || '',
      contactDepartment: data.companies?.[0]?.contact_department || '',

      // 請求先情報
      isBillingSameAsCompany: data.is_billing_same_as_company || false,
      billingPostalCode: data.billing?.postal_address || '',
      billingAddress: data.billing?.address || '',
      billingTel: data.billing?.tel || '',
      billingDepartment: data.billing?.department || '',
      billingContactName: data.billing?.contact_name || '',
      billingContactNameKana: data.billing?.contact_name_kana || '',
      billingContactTel: data.billing?.contact_tel || '',
      billingContactEmail: data.billing?.contact_email || '',

      // 追加法人（最初の1社を除く）
      additionalCompanies: (data.companies || []).slice(1).map((company: any) => ({
        id: String(company.id),
        name: company.name || '',
        nameKana: company.name_kana || '',
        postalCode: company.postal_code || '',
        address: company.address || '',
        tel: company.tel || '',
        contactName: company.contact_name || '',
        contactNameKana: company.contact_name_kana || '',
        contactTel: company.contact_tel || '',
        contactEmail: company.contact_email || '',
        contactDepartment: company.contact_department || ''
      })),

      // プラン・オプション
      initialCostLabel: data.initial_costs?.[0]?.name || '初期費用',
      initialCostPrice: data.initial_costs?.[0]?.price || 0,
      initialCostPlanId: data.initial_costs?.[0]?.plan_id,
      basicPlanLabel: data.plans?.[0]?.name || 'AI Travel 出張管理システム サービス利用基本料金',
      basicPlanPrice: data.plans?.[0]?.price || 0,
      basicPlanId: data.plans?.[0]?.plan_id || 1,
      showSupportFee: data.show_support_fee || false,
      supportFeeName: data.support_fee_name || 'サポート手数料',
      invoicePaymentRequested:
        data.invoice_payment_requested !== undefined ? data.invoice_payment_requested : true,
      dynamicOptions: (data.options || [])
        .filter((opt: any) => opt.price > 0) // 価格が設定されているものだけをdynamicOptionsとして扱う
        .map((opt: any) => ({
          id: String(opt.id),
          optionId: String(opt.option_plan_id),
          price: opt.price
        })),

      // 支払い口座
      bankName: data.billing?.bank_name || '',
      branchName: data.billing?.branch_name || '',
      accountType:
        data.billing?.account_type === 'ordinary' ? '1' : data.billing?.account_type === 'checking' ? '2' : '',
      accountNumber: data.billing?.account_number || '',
      accountHolderName: data.billing?.account_holder_name || '',

      // コメント・与信
      existence: data.existence || '',
      creditDownComment: data.credit_down_comment || '',
      blockingFactorComment: data.blocking_factor_comment || '',
      overrideJustificationComment: data.override_justification_comment || '',
      otherComment: data.other_comment || '',
      potentialGmv: data.potential_gmv || 0,
      salesforceUrl: data.salesforce_url || '',
      slackUrl: data.slack_url || '',
      applicationDate: ''
    };
  }

  /**
   * APIレスポンスからAdminFormDataに変換する
   */
  static convertApiResponseToAdminFormData(response: OnlineApplicationApiResponse): OnlineApplicationFormData {
    const oldFormData = this.convertApiResponseToFormData(response);

    return {
      ...oldFormData,
      basicPlanId: response.plans?.[0]?.plan_id?.toString() || '',
      creditComment: response.credit_comment || '',
      salesforceUrl: response.salesforce_url || '',
      slackUrl: response.slack_url || '',
      invoicePaymentRequested:
        response.invoice_payment_requested !== undefined ? response.invoice_payment_requested : true,
      showSupportFee: response.show_support_fee || false
    };
  }

  /**
   * APIレスポンスからドメインモデルへの変換
   */
  static fromApiResponse(response: OnlineApplicationApiResponse): OnlineApplication {
    const args = this.convertApiResponseToArgs(response);
    return new OnlineApplication(args);
  }

  /**
   * APIレスポンスからOnlineApplicationArgsへの変換
   */
  private static convertApiResponseToArgs(response: OnlineApplicationApiResponse): OnlineApplicationArgs {
    const firstCompany = response.companies?.[0] || {};
    const billing = response.billing;

    return {
      id: response.id,
      status: response.status,
      applicantId: response.applicant_id,
      startDesiredMonth: response.start_desired_month,
      potentialGmv: response.potential_gmv,
      salesforceUrl: response.salesforce_url,
      slackUrl: response.slack_url,
      invoicePaymentRequested: response.invoice_payment_requested,
      expirationDate: response.expiration_date,
      applicationEmailAddress: response.application_email_address,
      showSupportFee: response.show_support_fee,
      existence: response.existence,
      creditDownComment: response.credit_down_comment,
      blockingFactorComment: response.blocking_factor_comment,
      overrideJustificationComment: response.override_justification_comment,
      otherComment: response.other_comment || '',
      isBillingSameAsCompany: response.is_billing_same_as_company,
      appliedAt: response.applied_at,
      approvedAt: response.approved_at,
      createdAt: response.created_at,
      updatedAt: response.updated_at,

      // 会社情報（最初の会社のデータを使用）
      companyName: firstCompany.name || '',
      companyNameKana: firstCompany.name_kana || '',
      companyPostalCode: firstCompany.postal_code || '',
      companyAddress: firstCompany.address || '',
      companyTel: firstCompany.tel || '',
      contactName: firstCompany.contact_name || '',
      contactNameKana: firstCompany.contact_name_kana || '',
      contactTel: firstCompany.contact_tel || '',
      contactEmail: firstCompany.contact_email || '',
      contactDepartment: firstCompany.contact_department || '',

      // 請求先情報
      billingPostalCode: billing?.postal_address || '',
      billingAddress: billing?.address || '',
      billingTel: billing?.tel || '',
      billingDepartment: billing?.department || '',
      billingContactName: billing?.contact_name || '',
      billingContactNameKana: billing?.contact_name_kana || '',
      billingContactTel: billing?.contact_tel || '',
      billingContactEmail: billing?.contact_email || '',

      // 銀行情報
      bankName: billing?.bank_name || '',
      branchName: billing?.branch_name || '',
      accountType: billing?.account_type || '',
      accountNumber: billing?.account_number || '',
      accountHolderName: billing?.account_holder_name || '',

      // 関連データ
      companies: response.companies,
      billing: response.billing
        ? {
            ...response.billing,
            is_billing_same_as_company: response.is_billing_same_as_company || false
          }
        : null,
      initialCostPrice: response.initial_costs?.[0]?.price || 0,
      plans: response.plans,
      options: response.options,
      initialCosts: response.initial_costs
    };
  }
}

export default OnlineApplication;
